import React, { useEffect, useReducer } from 'react';
import { ContentAdDesktop } from 'app/styles/content';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { TITLE_POSTFIX } from 'app/seo/seo.constants';
import { useInfiniteScrollComponentState } from 'app/hooks/useInfiniteScroll';
import { useHistory, useParams } from 'react-router';
import { MatchCalendarDefaultPageReducer } from 'app/routes/matchCalendar/MatchCalendarPageReducer';
import { useFetchData, useRestoreData } from 'app/hooks/dataHooks';
import { fetchMatchesByDistrict, infinitelyFetchMatchesByDistrict, overwriteMatchCalendarMatches } from 'app/routes/matchCalendar/MatchCalendarPageActions';
import { DateNavigation } from 'app/components/matchCalendar/DateNavigation';
import { BackToStartButton } from 'app/components/matchCalendar/BackToStartButton';
import { format } from 'date-fns';
import deLocale from 'date-fns/locale/de';
import { useLogOutdatedContent } from 'app/hooks/useLogOutdatedContent';
import SeoFooter from 'app/components/matchCalendar/SeoFooter';
import DateModel from 'app/models/DateModel';
import { reduxSelectDistrict } from 'app/hooks/reduxSelectDistrict';
import styled from 'styled-components';
import DistrictCompetitionMatchdays from 'app/components/matchCalendar/DistrictCompetitionMatchdays';
import ProCompetitionMatchdays from 'app/components/matchCalendar/ProCompetitionMatchdays';
const SCContentAdDesktop = styled(ContentAdDesktop).withConfig({
  componentId: "sc-1d8ty2e-0"
})([""]);
const getState = (state, districtSlug) => {
  const {
    districtMatches,
    proMatches
  } = state;
  const districtMatchItems = districtMatches.items;
  const isFetchingDistrictMatches = districtMatches.isFetching;
  const districtMatchesError = districtMatches.error;
  const districtMatchesNextUrl = districtMatches.nextUrl;
  let proMatchItems = null;
  let isFetchingProMatches = false;
  let proMatchesError = null;
  const isExcludedDistrict = districtSlug === 'profis' || districtSlug === 'internationaler-fussball';
  if (!isExcludedDistrict && districtMatchItems && districtMatchesNextUrl === null) {
    proMatchItems = proMatches.items;
    isFetchingProMatches = proMatches.isFetching;
    proMatchesError = proMatches.error;
  }
  return {
    districtMatchItems,
    proMatchItems,
    isFetchingDistrictMatches,
    isFetchingProMatches,
    districtMatchesError,
    proMatchesError
  };
};
const seoTitleAndDescription = (district, date) => {
  const defaultTitlePart = 'Spiele & Ergebnisse';
  const defaultDescPart = function () {
    let status = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'aktuellen';
    return "Alle ".concat(status, " Spiele und Ergebnisse der Vereine aus der Region ").concat(district, " auf einen Blick.");
  };
  return {
    default: {
      title: defaultTitlePart,
      description: defaultDescPart()
    },
    default_live: {
      title: "".concat(defaultTitlePart, " - Live"),
      description: "Heute live: ".concat(defaultDescPart())
    },
    default_cancelled: {
      title: "".concat(defaultTitlePart, " - Abgesagt"),
      description: "".concat(defaultDescPart('abgesagten'))
    },
    date: {
      title: "".concat(defaultTitlePart, " - ").concat(date),
      description: defaultDescPart()
    },
    date_live: {
      title: "".concat(defaultTitlePart, " - Live am ").concat(date),
      description: defaultDescPart()
    },
    date_cancelled: {
      title: "".concat(defaultTitlePart, " - Abgesagt am ").concat(date),
      description: defaultDescPart()
    }
  };
};
const MatchCalendarDefaultPage = _ref => {
  let {
    date,
    filters
  } = _ref;
  const {
    restoredData
  } = useRestoreData('MatchCalendarDefaultPage');
  const {
    districtSlug
  } = useParams();
  const history = useHistory();
  const {
    name: districtName
  } = useSelector(reduxSelectDistrict);
  const dispatchRedux = useDispatch();
  const initialData = {
    date: '',
    filter: filters,
    districtMatches: {
      isFetching: false,
      items: [],
      nextUrl: '',
      error: null
    },
    proMatches: {
      isFetching: false,
      items: [],
      nextUrl: '',
      error: null
    }
  };
  const initialState = restoredData !== null && restoredData !== void 0 ? restoredData : initialData;
  const [state, dispatch] = useReducer(MatchCalendarDefaultPageReducer, initialState);
  const loadData = abortController => {
    fetchMatchesByDistrict(districtSlug, dispatchRedux, date, filters, abortController)(dispatch, state);
  };
  const infiniteLoadData = () => {
    infinitelyFetchMatchesByDistrict()(dispatch, state);
  };
  const {
    districtMatchItems,
    isFetchingDistrictMatches,
    districtMatchesError,
    proMatchItems,
    isFetchingProMatches,
    proMatchesError
  } = getState(state, districtSlug);
  const dataLength = districtMatchItems.length || (proMatchItems === null || proMatchItems === void 0 ? void 0 : proMatchItems.length);
  useInfiniteScrollComponentState(infiniteLoadData, dataLength);
  useFetchData(state, loadData, 'MatchCalendarDefaultPage');
  useLogOutdatedContent();
  useEffect(() => {
    const abortController = new AbortController();
    if (state.date && date !== state.date || state.filter !== filters) {
      dispatch(overwriteMatchCalendarMatches(initialData));
      loadData(abortController);
    }
    // Reset scroll position when navigating between dates
    window.scrollTo(0, 0);
    return () => {
      abortController.abort();
    };
  }, [date, filters]);
  const handleChange = date => {
    const val = /\d{4}-\d{2}-\d{2}/.test(date) ? date : format(date, 'yyyy-MM-dd', {
      locale: deLocale
    });
    // Replace url with new date only if it is different from the current one to avoid destroying new ad slots
    if (val !== state.date) {
      history.replace({
        search: "?date=".concat(val)
      });
    }
  };
  const isToday = date === new Date().toISOString().substring(0, 10);
  let operator = date && !isToday ? 'date' : 'default';
  operator += filters ? "_".concat(filters) : '';
  const seoInfo = seoTitleAndDescription(districtName, date)[operator];
  const title = "".concat(seoInfo.title, " - ").concat(districtName).concat(TITLE_POSTFIX);
  const meta = [{
    name: 'description',
    content: seoInfo.description
  }, {
    name: 'robots',
    content: isToday ? 'index, nofollow' : 'noindex, follow'
  }];

  // to prevent cls, only show AD-Container AND Footer-Link (for Crawler) if:
  // - district-matches are available and loaded for the day
  const hasDistrictMatches = !!districtMatchItems.length;
  // or
  // - pro-matches are available loaded for the day
  const hasProMatches = !!(proMatchItems !== null && proMatchItems !== void 0 && proMatchItems.length);
  // or
  // - there are neither district-matches nor pro-matches available for the day
  const noMatchesAvailable = !hasDistrictMatches && !isFetchingDistrictMatches && !hasProMatches && !isFetchingProMatches;
  const showFooter = hasDistrictMatches || hasProMatches || noMatchesAvailable;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SCContentAdDesktop, {
    bgColor: true,
    top: "7.125rem",
    contentPaddingBottom: '4.5rem',
    "data-sentry-element": "SCContentAdDesktop",
    "data-sentry-source-file": "MatchCalendarDefaultPage.tsx"
  }, /*#__PURE__*/React.createElement(Helmet, {
    title: title,
    meta: meta,
    "data-sentry-element": "Helmet",
    "data-sentry-source-file": "MatchCalendarDefaultPage.tsx"
  }), /*#__PURE__*/React.createElement(BackToStartButton, {
    "data-sentry-element": "BackToStartButton",
    "data-sentry-source-file": "MatchCalendarDefaultPage.tsx"
  }), /*#__PURE__*/React.createElement(DateNavigation, {
    categories: filters,
    date: date,
    onChange: handleChange,
    "data-sentry-element": "DateNavigation",
    "data-sentry-source-file": "MatchCalendarDefaultPage.tsx"
  }), /*#__PURE__*/React.createElement(DistrictCompetitionMatchdays, {
    competitionMatchdays: districtMatchItems,
    isFetching: isFetchingDistrictMatches,
    districtName: districtName,
    isError: !!districtMatchesError,
    "data-sentry-element": "DistrictCompetitionMatchdays",
    "data-sentry-source-file": "MatchCalendarDefaultPage.tsx"
  }), /*#__PURE__*/React.createElement(ProCompetitionMatchdays, {
    items: proMatchItems,
    isFetching: isFetchingProMatches,
    isError: !!proMatchesError,
    "data-sentry-element": "ProCompetitionMatchdays",
    "data-sentry-source-file": "MatchCalendarDefaultPage.tsx"
  }), showFooter ? /*#__PURE__*/React.createElement(SeoFooter, {
    districtSlug: districtSlug,
    activeFilter: filters,
    date: DateModel.fromIso8601(date)
  }) : null));
};
export default MatchCalendarDefaultPage;